import Map, {
  Popup,
  NavigationControl,
  MapRef,
  ViewStateChangeEvent,
  Marker,
  ViewState,
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Config } from '@dispatch/web/config';
import { BBox, lineString } from '@turf/turf';
import bbox from '@turf/bbox';
import {
  LngLatBoundsLike,
  LngLatLike,
  Popup as Tooltip,
  Marker as MapMarker,
} from 'mapbox-gl';
import { Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMap } from '@dispatch/shared/hooks';
import {
  PopupInfo,
  MapContext,
  MapPopupInfo,
  GeoJSONObject,
  Resource,
  TaskListParams,
  Task,
  TaskInfo,
} from '@dispatch/shared/types';
import React from 'react';
import useSupercluster from 'use-supercluster';
import { Pin } from './Pin';
import { useRadiusLayer } from './UseRadiusLayer';
import { GeoJsonProperties, LineString } from 'geojson';
import { Feature, Polygon, Properties } from '@turf/helpers';
import { groupTaskReducer, itemToGeoJSONPoint } from '@dispatch/shared';
import { isUndefined } from 'lodash';

export const PopupStyled = styled(Popup)({
  '.mapboxgl-popup-content': {
    padding: '10px 10px 2px',
  },
});

const centerOfUnitedStates: LngLatLike = [
  -99.22066676596688, 39.33623489212016,
];

export const DispatchMap = ({
  multiSelected,
  view,
  loading,
}: {
  multiSelected?: (Resource & { events?: Task[] })[];
  view?: TaskListParams;
  loading?: boolean;
}) => {
  const mapContext: MapContext = useMap();
  const theme = useTheme();
  // Map pin/cluster colors come from the single shared theme source so their
  // meaning is defined in one place. The basemap stays LIGHT in both modes
  // (no setStyle), so these are tuned for the light basemap in dark mode too.
  // Fallback to the historical literal so a bare `createTheme()` (no Dispatch
  // semantic tokens) degrades to the prior color instead of throwing.
  const clusterColor = theme.palette.semantic?.mapPin?.cluster ?? '#1876D1';
  const [map, setMap] = React.useState<MapRef | null>(null);
  const navigate = useNavigate();
  const mapRef2 = (ref: MapRef) => setMap(ref);
  const [bounds, setBounds] = React.useState<number[][]>();
  const [points, setPoints] = React.useState<any>([]);
  const [schedulerGeoJson, setSchedulerGeoJson] = React.useState<
    GeoJSONObject[]
  >([]);

  React.useEffect(() => {
    if (!map?.isStyleLoaded()) return;
    setPoints([]);
    const tasks = multiSelected?.flatMap(
      (selected) => selected.events
    ) as unknown;

    if (tasks && Array.isArray(tasks) && tasks.length > 0) {
      const groupTasks: Record<string, Partial<TaskInfo>[]> = (
        tasks || []
      )?.reduce(groupTaskReducer, {});

      const geoJsonTasks: GeoJSONObject[] =
        Object.entries(groupTasks).flatMap(itemToGeoJSONPoint);

      setSchedulerGeoJson(geoJsonTasks);
      setPoints(geoJsonTasks);
    }
  }, [multiSelected]);

  React.useEffect(() => {
    // determine and set bounds from markers.
    let geoJson = mapContext.geoJson || [];

    if (schedulerGeoJson.length > 0) {
      geoJson = schedulerGeoJson;
    }

    const coords = geoJson.map((point: GeoJSONObject) => {
      return point.geometry.coordinates;
    });

    if (coords && coords?.length > 0) {
      const coordinateString = lineString([...coords, []]);

      boundBox(coordinateString);
    }
  }, [mapContext.geoJson, schedulerGeoJson]);

  /**
   *  lineString requests an array of 2 sets or more of coordinates, or else it will error out.
   * the fallback is the default coordinates of the map [-99.22066676596688, 39.33623489212016]. The reason this is needed is if only
   * 1 pin is on the map it will only have an array of 1 set of coordinates, and lineString does not
   * like this.
   */
  const boundBox = (coordinateString: Feature<LineString, Properties>) => {
    /**
     * bbox takes the built coordinates from lineString, and outputs the bounding box for the map to use.
     */
    const [minLng, minLat, maxLng, maxLat] = bbox(coordinateString);
    const southWest = [minLng, minLat];
    const northEast = [maxLng, maxLat];
    const bounds = [southWest, northEast];

    setBounds(bounds);
  };

  React.useEffect(() => {
    // fit the map to the bounds based on the bounds calculated
    if (bounds) {
      map?.fitBounds(bounds as LngLatBoundsLike, { padding: 40 });
    }
  }, [bounds, map]);

  const [viewState, setViewState] = React.useState<ViewState>();
  const bbounds = map ? map.getMap().getBounds().toArray().flat() : null;

  function noPointsToZoom(data?: GeoJSONObject[]) {
    if (!data) return false;
    return data && data?.length <= 0;
  }

  React.useEffect(() => {
    if (noPointsToZoom(mapContext.geoJson)) {
      map?.flyTo({
        center: centerOfUnitedStates,
        zoom: 2,
        duration: 0,
      });
    }
    setPoints(mapContext.geoJson);
  }, [centerOfUnitedStates, map, mapContext.geoJson]);

  const { clusters } = useSupercluster({
    points,
    bounds: bbounds as BBox,
    zoom: viewState?.zoom || 12,
    options: { radius: 75, maxZoom: 20 },
  });

  const renderClusters = React.useMemo(() => {
    return clusters?.map((cluster) => {
      const [longitude, latitude] = cluster.geometry.coordinates;
      const { cluster: isCluster, point_count: pointCount } =
        cluster.properties;

      const propertiesArr = cluster?.properties?.['data']
        ? Object.entries(cluster?.properties?.['data']).map(
            (property: [string, unknown]) => property[1]
          )
        : [];

      if (isCluster) {
        return (
          <Marker key={cluster.id} latitude={latitude} longitude={longitude}>
            <div
              className="cluster-marker"
              style={{
                backgroundColor: clusterColor,
                height: '30px',
                width: '30px',
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                padding: '10px',
              }}
            >
              {pointCount}
            </div>
          </Marker>
        );
      }

      return (
        <Marker
          key={cluster.properties['id']}
          longitude={longitude}
          latitude={latitude}
          anchor="bottom"
          onClick={(e) => {
            e.originalEvent.stopPropagation();

            if (propertiesArr.length > 1) {
              mapContext.setPopupInfo({
                data: propertiesArr as PopupInfo[],
                lossLong: longitude,
                lossLat: latitude,
              });
            } else {
              mapContext.setPopupInfo(propertiesArr[0] as MapPopupInfo);
            }
          }}
        >
          {propertiesArr.length === 1 && <Pin />}
          {viewState && propertiesArr.length > 1 && viewState.zoom < 16 && (
            <Pin />
          )}
          {viewState && propertiesArr.length > 1 && viewState.zoom > 16 && (
            <div
              className="cluster-marker"
              style={{
                width: `30px`,
                height: `30px`,
                backgroundColor: clusterColor,
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
              }}
            >
              {propertiesArr.length}
            </div>
          )}
        </Marker>
      );
    });
  }, [clusters, mapContext, viewState, points, clusterColor]);

  const { circles, overlappingCords } = useRadiusLayer({
    multiSelected,
  });

  React.useEffect(() => {
    if (circles && circles.length <= 0) return;

    const coords = (circles || []).map((circle) => {
      return [
        circle?.properties?.['rightEdgeCoords'],
        circle?.properties?.['leftEdgeCoords'],
      ];
    });

    if (coords.length > 0) {
      const coordinateString = lineString([...coords, []].flat());
      boundBox(coordinateString);
    }
  }, [circles.length]);

  const tearDownSource = () => {
    if (map?.getLayer('circleLine')) {
      map?.getMap().removeLayer('circleLine');
    }
    if (map?.getLayer('circleBuffer')) {
      map?.getMap().removeLayer('circleBuffer');
    }
    if (map?.getSource('circles')) {
      map?.getMap().removeSource('circles');
    }
  };

  const [allmarkers, setAllMarkers] = React.useState<MapMarker[]>([]);

  React.useEffect(() => {
    allmarkers.forEach((marker) => marker.remove());
  }, [circles]);

  React.useEffect(() => {
    allmarkers.forEach((marker) => marker.remove());
    tearDownSource();
  }, [view?.showMap, view?.view]);

  const addStartingPointMarkers = (
    circleArray: Feature<Polygon, GeoJsonProperties>[]
  ) => {
    return circleArray?.forEach((circle, index) => {
      if (circle?.properties?.['isStartingLocation']) {
        const center = circle?.properties?.['center'];
        const resourceName = circle?.properties?.['name'];
        if (map?.getMap()) {
          const popup = new Tooltip({ offset: 25 }).setText(
            `${resourceName} starting location`
          );

          const marker = new MapMarker({ color: '#2F7C31' })
            .setLngLat(center)
            .setPopup(popup)
            .addTo(map.getMap());

          const all = allmarkers;
          all.push(marker);
          setAllMarkers(all);
        }
      }
    });
  };

  const addSource = () => {
    if (!map?.isStyleLoaded()) return;
    map?.getMap().addSource('circles', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: circles,
      },
    });

    const query = (bool: boolean) => [
      '==',
      ['get', 'isStartingLocation'],
      bool,
    ];
    map?.getMap().addLayer({
      id: 'circleBuffer',
      type: 'line',
      source: 'circles',
      paint: {
        'line-color': 'transparent',
        'line-width': 25,
      },
      filter: query(false),
    });

    map?.getMap().addLayer({
      id: 'circleLine',
      type: 'line',
      source: 'circles',
      paint: {
        'line-color': '#5A5A5A',
        'line-width': 2,
      },
      filter: query(false),
    });
    addStartingPointMarkers(circles);
  };

  const addPopup = () => {
    let popup: Tooltip;
    if (!map?.isStyleLoaded) return;
    map?.getMap().on('mouseenter', 'circleBuffer', (e) => {
      map.getCanvas().style.cursor = 'pointer';

      // the coordinates that makes the popup render at the bottom of the circle
      const coordinates =
        e?.features?.[0]?.properties?.['destinationCoordinates'];
      // default to this name if no overlapping coordinates array is found
      let name = e?.features?.[0]?.properties?.['name'];

      // gets name, and renders multiple names if coordinates are overlapping
      if (
        overlappingCords[`${e?.features?.[0]?.properties?.['coordinatesKey']}`]
          ?.length > 0
      ) {
        name =
          overlappingCords[
            `${e?.features?.[0]?.properties?.['coordinatesKey']}`
          ].join(', ');
      }

      if (coordinates) {
        // render popup on map
        popup = new Tooltip()
          .setLngLat(JSON.parse(coordinates))
          .setHTML(name)
          .addTo(map.getMap());
      }
    });

    map?.getMap().on('mouseleave', 'circleBuffer', (e) => {
      map.getCanvas().style.cursor = '';

      popup.remove();
    });
  };

  React.useEffect(() => {
    // when new data loads we have to tear down the source so that it doesnt try to add two. This results in a mapbox error
    tearDownSource();
    if (loading) return;

    addSource();
    addPopup();
  }, [multiSelected, loading]);

  React.useEffect(() => {
    if (view?.view === 'list') {
      tearDownSource();
    }
  }, [view?.view]);

  React.useEffect(() => {
    const source = map?.getMap().getSource('circles');
    if (isUndefined(source)) {
      map?.easeTo({
        center: [centerOfUnitedStates[0], centerOfUnitedStates[1]],
        zoom: 2,
      });
    }
  }, [map?.getMap().getSource('circles')]);

  return (
    <Map
      ref={mapRef2}
      style={{ width: '100%', height: '70vh' }}
      mapStyle="mapbox://styles/mapbox/streets-v11"
      mapboxAccessToken={Config.MapBoxAPIKey}
      attributionControl={false}
      maxZoom={20}
      onMove={(e: ViewStateChangeEvent) => setViewState(e?.viewState)}
      initialViewState={{
        // centers on Us map if no points exist.
        longitude: centerOfUnitedStates[0],
        latitude: centerOfUnitedStates[1],
        zoom: 5,
      }}
    >
      <NavigationControl />
      {view?.view === 'scheduler'
        ? multiSelected && multiSelected.length > 0 && renderClusters
        : renderClusters}
      {mapContext?.popupInfo?.data && (
        <PopupStyled
          anchor="top"
          longitude={mapContext?.popupInfo?.lossLong || 0}
          latitude={mapContext?.popupInfo?.lossLat || 0}
          onClose={() => {
            mapContext?.setPopupInfo(null);
          }}
        >
          <ul
            style={{
              maxHeight: '250px',
              overflow: 'scroll',
              listStyle: 'none',
              padding: '0',
              margin: '0',
              textAlign: 'center',
            }}
          >
            {mapContext?.popupInfo?.data?.map((item: PopupInfo) => {
              return (
                <li>
                  <Typography component="p" sx={{ fontWeight: 'bold' }}>
                    {item?.claimNumber || item?.id || ''}
                  </Typography>
                  <Typography component="p">
                    {item?.insuredName || item?.name || ''}
                  </Typography>
                  <Button
                    variant="text"
                    sx={{
                      paddingLeft: 0,
                      fontSize: '0.75rem',
                    }}
                    onClick={() => {
                      navigate(`/tasks/${item?.id}`);
                    }}
                  >
                    Show Details
                  </Button>
                </li>
              );
            })}
          </ul>
        </PopupStyled>
      )}
      {mapContext?.popupInfo?.claimNumber && (
        <PopupStyled
          anchor="top"
          longitude={mapContext?.popupInfo?.lossLong || 0}
          latitude={mapContext?.popupInfo?.lossLat || 0}
          onClose={() => {
            mapContext?.setPopupInfo(null);
          }}
        >
          <Stack alignItems={'center'} justifyContent={'center'}>
            <Typography component="p" sx={{ fontWeight: 'bold' }}>
              {mapContext?.popupInfo?.claimNumber ||
                mapContext?.popupInfo?.id ||
                ''}
            </Typography>
            <Typography component="p">
              {mapContext?.popupInfo?.insuredName ||
                mapContext?.popupInfo?.name ||
                ''}
            </Typography>
            <Button
              variant="text"
              sx={{
                paddingLeft: 0,
                fontSize: '0.75rem',
              }}
              onClick={() => {
                navigate(`/tasks/${mapContext?.popupInfo?.id}`);
              }}
            >
              Show Details
            </Button>
          </Stack>
        </PopupStyled>
      )}
    </Map>
  );
};
